/**
 * Rail topology utilities (Feature Area 2.2 — Rail Topology Editor)
 *
 * Provides geometry helpers plus two validators:
 *  - Clearance checker: flags rails colliding with equipment and equipment
 *    footprints overlapping each other.
 *  - Loop-closure validator: builds a connectivity graph from rail-segment
 *    endpoints and flags open ends that would strand a vehicle.
 */
import { LayoutElement, ElementType } from '../types';
import { getOhtPivot, getOhtPivotWorld } from './ohtPivot';
import { isLayoutEquipmentElement } from './layout3d';
import {
  EQUIPMENT_PORT_EDGE,
  STOCKER_PORT_EDGE,
  alignEquipmentPortsToLoadDocks,
  directionForEdge,
  portEdge,
  type Port,
  type PortEdge,
} from './ports';
import {
  STOCKER_CRANE_X_RATIO as SHARED_STOCKER_CRANE_X_RATIO,
  EQUIPMENT_DOCK_HANG_RATIO,
  STOCKER_TRAY_HANG_RATIO,
  resolveStockerDockIndex,
  resolveStockerDockIndexOrFirst,
  resolveStockerDockOffsetsX,
  type StockerDockRef,
} from './stockerDocks';
import type { EquipmentInstance } from '../features/equipment/equipmentTypes';
import { equipmentInstanceToLayoutElement, polygonBounds } from './equipmentGeometry';

export interface Pt {
  x: number;
  y: number;
}

export interface AABB {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export const RAIL_TYPE: ElementType = 'overhead-rail';
export const CURVE_RAIL_TYPE: ElementType = 'overhead-curve';
export const CROSS_RAIL_TYPE: ElementType = 'cross-rail';

export const ALL_RAIL_TYPES: ReadonlySet<ElementType> = new Set<ElementType>([
  RAIL_TYPE,
  CURVE_RAIL_TYPE,
  CROSS_RAIL_TYPE,
]);

export const EQUIPMENT_TYPES: ReadonlySet<ElementType> = new Set<ElementType>([
  'stocker',
  'stb',
  'ohb',
  'lifter',
]);

/** Default endpoint clustering / snapping tolerance in world units. */
export const ENDPOINT_TOLERANCE = 2;

/**
 * Validation should be much stricter than interactive snapping. A loose value
 * can hide visible DXF-scale gaps by treating nearby-but-broken rail ends as
 * connected.
 */
export const VALIDATION_ENDPOINT_TOLERANCE = 0.25;

/**
 * Larger attraction radius used during live drag so the element "sticks" to a
 * nearby endpoint before you release the mouse. Intentionally wider than
 * ENDPOINT_TOLERANCE so the user doesn't have to be pixel-perfect.
 */
export const SNAP_DRAG_TOLERANCE = 48;
export const OBJECT_EDGE_SNAP_TOLERANCE = 24;

/**
 * Breathing room kept between two equipment footprints. Tools whose footprints
 * touch read as one merged machine in 3D, because each one's dock and shutter
 * stand proud of the footprint edge and run into the neighbour's shell.
 */
export const EQUIPMENT_MIN_GAP = 6;

export type RailShapeType =
  | 'straight'
  | 'curve180'
  | 'curve90'
  | 'curve45'
  | 'branch'
  | 'lift'
  | 'connector'
  | 'junction-circle';

/**
 * Resolves rail shape from element custom properties with a stable fallback.
 * @param el - Layout element to resolve
 * @returns Rail shape type (straight, curve45, curve90, curve180, connector, junction-circle, branch, lift)
 */
export type TrackDirection = 'bi' | 'uni';

/** Resolve stored track direction; defaults to unidirectional. */
export function resolveTrackDirection(el: LayoutElement): TrackDirection {
  return el.customProperties?.direction === 'bi' ? 'bi' : 'uni';
}

export function isTrackUnidirectional(el: LayoutElement): boolean {
  return resolveTrackDirection(el) === 'uni';
}

export function resolveRailType(el: LayoutElement): RailShapeType {
  const raw = String(el.customProperties?.railType || '').toLowerCase();
  if (
    raw === 'curve180' ||
    raw === 'curve90' ||
    raw === 'curve45' ||
    raw === 'branch' ||
    raw === 'lift' ||
    raw === 'connector' ||
    raw === 'junction-circle'
  ) {
    return raw;
  }
  return 'straight';
}

/**
 * Short movable rail used to shift an OHT between parallel tracks.
 */
export const isConnectorRailType = (railType: RailShapeType): boolean => railType === 'connector';

/**
 * Straight-like rails that use length/thickness meters (not curve radius).
 */
export const isStraightLikeRailType = (railType: RailShapeType): boolean =>
  railType === 'straight' || railType === 'connector';

/**
 * Check if a rail type is a curve (curve45, curve90, or curve180).
 * @param railType - Rail shape type to check
 * @returns true if type is a curve rail
 */
export const isCurveRailType = (railType: RailShapeType): boolean =>
  railType === 'curve180' || railType === 'curve90' || railType === 'curve45';

/**
 * Check if a rail type is a junction (Y, T, X, or circular).
 * @param railType - Rail shape type to check
 * @returns true if type is a junction rail
 */
export const isJunctionRailType = (railType: RailShapeType): boolean =>
  railType === 'junction-circle';

/**
 * Get the sweep angle for a curve rail in degrees.
 * @param railType - Rail shape type
 * @returns Sweep angle: 180° for curve180, 90° for curve90, 45° otherwise
 */
export const getCurveSweepAngle = (railType: RailShapeType): number => {
  if (railType === 'curve180') return 180;
  if (railType === 'curve90') return 90;
  return 45;
};

/**
 * Get the sweep direction for a curve rail.
 * +1 = counter-clockwise (default), -1 = clockwise.
 * Determines which side of a curve180 semicircle bulges.
 * @param el - Layout element with optional curveSweepSign custom property
 * @returns Sweep sign: 1 for CCW, -1 for CW
 */
export function getCurveSweepSign(el: LayoutElement): 1 | -1 {
  const raw = el.customProperties?.curveSweepSign;
  if (raw === -1 || raw === '-1') return -1;
  return 1;
}

const FEET_TO_METERS = 0.3048;

export const CURVE_FIXED_LENGTH_FEET: Record<'curve45' | 'curve90' | 'curve180', number> = {
  curve45: 1,
  curve90: 1,
  curve180: 2,
};

/**
 * Get the fixed length of a curve rail in meters.
 * @param railType - Rail shape type
 * @returns Length in meters for curve rails, null for non-curve types
 */
export function getFixedCurveLengthMeters(railType: RailShapeType): number | null {
  if (railType === 'curve45') return CURVE_FIXED_LENGTH_FEET.curve45 * FEET_TO_METERS;
  if (railType === 'curve90') return CURVE_FIXED_LENGTH_FEET.curve90 * FEET_TO_METERS;
  if (railType === 'curve180') return CURVE_FIXED_LENGTH_FEET.curve180 * FEET_TO_METERS;
  return null;
}

/**
 * Get the fixed radius of a curve rail in meters.
 * Calculated from arc length and sweep angle.
 * @param railType - Rail shape type
 * @returns Radius in meters for curve rails, null for non-curve types
 */
export function getFixedCurveRadiusMeters(railType: RailShapeType): number | null {
  const lengthMeters = getFixedCurveLengthMeters(railType);
  if (!(lengthMeters && lengthMeters > 0)) return null;
  const sweepDeg = getCurveSweepAngle(railType);
  const sweepRad = (sweepDeg * Math.PI) / 180;
  if (!(sweepRad > 0)) return null;
  return lengthMeters / sweepRad;
}

const clampPortCount = (value: unknown, fallback: number): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(1, Math.min(12, Math.round(n)));
};

/**
 * Get the maximum number of ports for a circular junction.
 * Clamped between 1 and 8 based on element customProperties.maxPorts.
 * @param el - Layout element (must be a junction-circle)
 * @returns Maximum port count (1-8)
 */
export function getCircleJunctionMaxPorts(el: LayoutElement): number {
  const custom = el.customProperties?.maxPorts;
  if (custom !== undefined && custom !== null) {
    const n = Number(custom);
    if (Number.isFinite(n)) {
      return Math.max(1, Math.min(8, Math.round(n)));
    }
  }
  const inCount = el.customProperties?.junctionInputs ?? el.customProperties?.inputs;
  const outCount = el.customProperties?.junctionOutputs ?? el.customProperties?.outputs;
  if (inCount !== undefined || outCount !== undefined) {
    const total = clampPortCount(inCount, 2) + clampPortCount(outCount, 2);
    return Math.max(1, Math.min(8, total));
  }
  return 8; // Default: full 8-way hub (N/NE/E/SE/S/SW/W/NW)
}

/** Outer rail ring as a fraction of min(width, height). */
export const CIRCLE_JUNCTION_RING_RATIO = 0.22;

export function getCircleJunctionRingRadius(width: number, height: number): number {
  return Math.max(0.001, Math.min(width, height) * CIRCLE_JUNCTION_RING_RATIO);
}

/** Distance from junction center to bbox edge along `angleDeg` (layout deg, 0 = +X). */
export function getCircleJunctionTipRadiusAtAngle(
  width: number,
  height: number,
  angleDeg: number
): number {
  const cx = width / 2;
  const cy = height / 2;
  const norm = (((angleDeg % 360) + 360) % 360) * (Math.PI / 180);
  const dx = Math.cos(norm);
  const dy = Math.sin(norm);

  const tx =
    Math.abs(dx) < 1e-9
      ? Number.POSITIVE_INFINITY
      : dx > 0
        ? (width - cx) / dx
        : (0 - cx) / dx;
  const ty =
    Math.abs(dy) < 1e-9
      ? Number.POSITIVE_INFINITY
      : dy > 0
        ? (height - cy) / dy
        : (0 - cy) / dy;

  return Math.max(0, Math.min(tx, ty));
}

/** Max distance from junction center to any port tip (bbox edge along a spoke). */
export function getCircleJunctionMaxTipRadius(
  width: number,
  height: number,
  maxPorts: number
): number {
  let maxD = 0;
  for (const s of getCircleJunctionSpokesLocal(width, height, maxPorts)) {
    maxD = Math.max(maxD, getCircleJunctionTipRadiusAtAngle(width, height, s.angle));
  }
  return maxD;
}

/** Nudge past spoke tips so dual-rail ends meet incoming track (keep small to avoid overlap). */
export const CIRCLE_JUNCTION_BAR_SEAM_OVERLAP = 0.18;

/** Half-length of the rotating bridge for a given bar axis (layout deg). */
export function getCircleJunctionBarHalfExtent(
  width: number,
  height: number,
  barAngleDeg: number,
  trackWidth = 0
): number {
  const tip = getCircleJunctionTipRadiusAtAngle(width, height, barAngleDeg);
  const railReach = Math.max(0, trackWidth) * 0.5;
  return tip + railReach + CIRCLE_JUNCTION_BAR_SEAM_OVERLAP;
}

/**
 * Turntable bridge half-length constrained to the hub disk (reference 2D/3D look).
 * Incoming straights extend to the ring; the bridge rotates inside the circle only.
 */
export function getCircleJunctionTurntableBarHalfInRing(
  width: number,
  height: number,
  trackWidth = 0
): number {
  const ringR = getCircleJunctionRingRadius(width, height);
  const capInset = Math.min(Math.max(trackWidth * 0.12, 0.15), ringR * 0.12);
  return Math.max(ringR - capInset, ringR * 0.94);
}

/**
 * Bar axis that best spans connected through ports (opposing pair when possible).
 */
export function getCircleJunctionThroughBarAngleDeg(
  junction: LayoutElement,
  rails: readonly LayoutElement[] = []
): number {
  const w = junction.dimensions.width;
  const h = junction.dimensions.height;
  const maxPorts = getCircleJunctionMaxPorts(junction);
  const spokes = getCircleJunctionSpokesLocal(w, h, maxPorts);
  const connected = getCircleJunctionConnectedPortIndices(junction, rails);
  const indices = [...connected].sort((a, b) => a - b);

  if (indices.length >= 2) {
    let bestAngle = spokes[indices[0]!]?.angle ?? 0;
    let bestScore = Number.POSITIVE_INFINITY;
    for (let i = 0; i < indices.length; i++) {
      for (let j = i + 1; j < indices.length; j++) {
        const a1 = spokes[indices[i]!]?.angle ?? 0;
        const a2 = spokes[indices[j]!]?.angle ?? 0;
        let diff = Math.abs(a1 - a2) % 360;
        if (diff > 180) diff = 360 - diff;
        const score = Math.abs(diff - 180);
        if (score < bestScore) {
          bestScore = score;
          bestAngle = a1;
        }
      }
    }
    return bestAngle;
  }

  if (indices.length === 1) {
    return spokes[indices[0]!]?.angle ?? 0;
  }

  return spokes[0]?.angle ?? 0;
}

export type CircleJunctionSpokeLocal = {
  tipX: number;
  tipY: number;
  ringX: number;
  ringY: number;
  angle: number;
  kind?: 'input' | 'output';
};

export function getCircleJunctionSpokesLocal(
  width: number,
  height: number,
  maxPorts: number
): CircleJunctionSpokeLocal[] {
  const cx = width / 2;
  const cy = height / 2;
  const outerR = getCircleJunctionRingRadius(width, height);

  const toTip = (angleDeg: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    const dx = Math.cos(rad);
    const dy = Math.sin(rad);

    const tx =
      Math.abs(dx) < 1e-9
        ? Number.POSITIVE_INFINITY
        : dx > 0
          ? (width - cx) / dx
          : (0 - cx) / dx;
    const ty =
      Math.abs(dy) < 1e-9
        ? Number.POSITIVE_INFINITY
        : dy > 0
          ? (height - cy) / dy
          : (0 - cy) / dy;

    const t = Math.max(0, Math.min(tx, ty));
    return {
      tipX: cx + dx * t,
      tipY: cy + dy * t,
      ringX: cx + dx * outerR,
      ringY: cy + dy * outerR,
      angle: angleDeg,
    };
  };

  const spokes = [];
  for (let i = 0; i < maxPorts; i++) {
    const angleDeg = i * (360 / maxPorts);
    spokes.push(toTip(angleDeg));
  }
  return spokes;
}

/**
 * Ports whose tip→ring stubs should be drawn / preferred for simulation.
 *
 * - `activePortIds` (from the insert that created the junction) stays eligible
 *   even before a neighbor rail is present — used by the track graph.
 * - Any spoke that currently has an abutting rail tip is activated automatically.
 * - Junctions with neither config nor abutting rails return an empty set (no
 *   unused stubs). Point Draw still offers all `maxPorts` tips via
 *   `getRailEndpoints`.
 *
 * For **drawing** stubs on the canvas, prefer
 * {@link getCircleJunctionConnectedPortIndices} so only physically connected
 * spokes render.
 */
export function getCircleJunctionActivePortIndices(
  junction: LayoutElement,
  rails: readonly LayoutElement[] = []
): Set<number> {
  const maxPorts = getCircleJunctionMaxPorts(junction);
  const active = new Set<number>();

  const configured = junction.customProperties?.activePortIds;
  if (Array.isArray(configured)) {
    configured.forEach((portId) => {
      const match = typeof portId === 'string' ? /^port-(\d+)$/.exec(portId) : null;
      const index = match ? Number(match[1]) : NaN;
      if (Number.isInteger(index) && index >= 0 && index < maxPorts) active.add(index);
    });
  }

  for (const index of getCircleJunctionConnectedPortIndices(junction, rails)) {
    active.add(index);
  }

  return active;
}

/**
 * Spoke indices that currently have an abutting rail tip.
 * Unused ports stay hidden even when `maxPorts` is 8 and Point Draw can still
 * snap to every tip.
 */
export function getCircleJunctionConnectedPortIndices(
  junction: LayoutElement,
  rails: readonly LayoutElement[] = []
): Set<number> {
  const maxPorts = getCircleJunctionMaxPorts(junction);
  const connected = new Set<number>();
  const junctionTips = getRailEndpoints(junction);
  rails.forEach((rail) => {
    if (rail.id === junction.id || !rail.metadata.visible || !ALL_RAIL_TYPES.has(rail.type)) {
      return;
    }
    getRailEndpoints(rail).forEach((railTip) => {
      junctionTips.forEach((junctionTip, index) => {
        if (index >= maxPorts) return;
        if (
          Math.hypot(railTip.x - junctionTip.x, railTip.y - junctionTip.y) <= ENDPOINT_TOLERANCE
        ) {
          connected.add(index);
        }
      });
    });
  });
  return connected;
}

/** Unit direction (world) pointing from rail end `endIndex` into the rail body. */
function railInwardDirAtEnd(rail: LayoutElement, endIndex: number): Pt | null {
  const r = ((rail.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  let lx: number;
  let ly: number;

  if (isCurveRailType(resolveRailType(rail))) {
    const sweepRad =
      (getCurveSweepSign(rail) * getCurveSweepAngle(resolveRailType(rail)) * Math.PI) / 180;
    const s = Math.sign(sweepRad) || 1;
    if (endIndex === 0) {
      lx = 0;
      ly = s;
    } else {
      lx = s * Math.sin(sweepRad);
      ly = -s * Math.cos(sweepRad);
    }
  } else {
    const ends = getRailEndpoints(rail);
    const from = ends[endIndex];
    const to = ends[endIndex === 0 ? 1 : 0];
    if (!from || !to) return null;
    const len = Math.hypot(to.x - from.x, to.y - from.y);
    if (len < 1e-9) return null;
    return { x: (to.x - from.x) / len, y: (to.y - from.y) / len };
  }

  return { x: lx * cos - ly * sin, y: lx * sin + ly * cos };
}

/** True when the rail leaves junction tip `jTip` heading radially away from the hub. */
function railLeavesAlongSpoke(
  rail: LayoutElement,
  endIndex: number,
  junction: LayoutElement,
  jTip: Pt
): boolean {
  const inward = railInwardDirAtEnd(rail, endIndex);
  if (!inward) return false;
  const jr = ((junction.rotation || 0) * Math.PI) / 180;
  const hw = junction.dimensions.width / 2;
  const hh = junction.dimensions.height / 2;
  const cx = junction.position.x + hw * Math.cos(jr) - hh * Math.sin(jr);
  const cy = junction.position.y + hw * Math.sin(jr) + hh * Math.cos(jr);
  const ox = jTip.x - cx;
  const oy = jTip.y - cy;
  const olen = Math.hypot(ox, oy);
  if (olen < 1e-9) return false;
  return (inward.x * ox + inward.y * oy) / olen > 0.985;
}

/**
 * Extend straight centerlines past snapped port tips toward the hub ring so 2D
 * tracks visually meet the turntable (tips sit on the junction bbox; the ring is inset).
 */
export function getStraightRailCircleJunctionReach(
  rail: LayoutElement,
  rails: readonly LayoutElement[] = [],
  trackWidth = 0
): { extendStart: number; extendEnd: number } {
  const railType = resolveRailType(rail);
  if (
    isJunctionRailType(railType) ||
    railType === 'connector' ||
    (!isStraightLikeRailType(railType) && !isCurveRailType(railType))
  ) {
    return { extendStart: 0, extendEnd: 0 };
  }

  const ends = getRailEndpoints(rail);
  let extendStart = 0;
  let extendEnd = 0;
  const seam = Math.max(CIRCLE_JUNCTION_BAR_SEAM_OVERLAP, trackWidth * 0.35);

  for (const junction of rails) {
    if (junction.id === rail.id || !junction.metadata.visible) continue;
    if (resolveRailType(junction) !== 'junction-circle') continue;

    const jw = junction.dimensions.width;
    const jh = junction.dimensions.height;
    const ringR = getCircleJunctionRingRadius(jw, jh);
    const maxPorts = getCircleJunctionMaxPorts(junction);
    const spokes = getCircleJunctionSpokesLocal(jw, jh, maxPorts);
    const jTips = getRailEndpoints(junction);

    for (let ei = 0; ei < ends.length; ei++) {
      const railTip = ends[ei]!;
      for (let pi = 0; pi < jTips.length; pi++) {
        const jTip = jTips[pi]!;
        if (Math.hypot(railTip.x - jTip.x, railTip.y - jTip.y) > ENDPOINT_TOLERANCE) continue;
        const spoke = spokes[pi];
        if (!spoke) continue;
        // Only rails that run straight out along the spoke may reach into the
        // hub; a rail meeting a tip at an angle (e.g. vertical rail on a
        // diagonal port) would otherwise overshoot along its own axis.
        if (!railLeavesAlongSpoke(rail, ei, junction, jTip)) continue;
        const tipR = getCircleJunctionTipRadiusAtAngle(jw, jh, spoke.angle);
        const reach = Math.max(0, tipR - ringR + Math.min(seam, trackWidth * 0.22));
        if (ei === 0) extendStart = Math.max(extendStart, reach);
        else extendEnd = Math.max(extendEnd, reach);
      }
    }
  }

  return { extendStart, extendEnd };
}

/** Instanced / mesh straight segment span and local center (extends into circle junction hubs). */
export function resolveStraightRail3DSpan(
  rail: LayoutElement,
  rails: readonly LayoutElement[],
  trackWidth: number
): { span: number; centerLocalX: number; centerLocalZ: number; alongX: boolean } {
  const w = rail.dimensions.width;
  const h = rail.dimensions.height;
  const alongX = w >= h;
  const base = alongX ? w : h;
  const { extendStart, extendEnd } = getStraightRailCircleJunctionReach(rail, rails, trackWidth);
  const seam = Math.max(trackWidth * 0.15, 0.25);
  return {
    span: base + extendStart + extendEnd + seam,
    centerLocalX: alongX ? (w + extendEnd - extendStart) / 2 : w / 2,
    centerLocalZ: alongX ? h / 2 : (h + extendEnd - extendStart) / 2,
    alongX,
  };
}

/**
 * Axis-aligned bounding box of an element accounting for its rotation.
 * The Konva renderer uses the element position as a top-left origin and
 * rotates around it, so corners are computed in that frame.
 */
export function getAABB(el: LayoutElement): AABB {
  const { x, y } = el.position;
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);

  const corners: Pt[] = [
    [0, 0],
    [w, 0],
    [w, h],
    [0, h],
  ].map(([cx, cy]) => ({
    x: x + cx * cos - cy * sin,
    y: y + cx * sin + cy * cos,
  }));

  const xs = corners.map((c) => c.x);
  const ys = corners.map((c) => c.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

export function aabbOverlap(a: AABB, b: AABB): boolean {
  return a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY;
}

/**
 * Returns the connection endpoints of a rail segment in world space.
 *
 * Straight rails: midpoints of the two short edges.
 *
 * Curve rails: the arc centre sits at the element position. The single-line
 * track path sweeps from 0° to the segment angle, so
 * the two endpoints are at:
 *   p1 = center + radius * (cos 0°, sin 0°)   → right side  (angle 0)
 *   p2 = center + radius * (cos θ,  sin θ)    → arc end     (angle θ)
 * Both are then rotated by the element's world rotation around the center.
 *
 * Circular junction: return all spoke tips.
 */
export function getRailEndpoints(el: LayoutElement): Pt[] {
  const { x, y } = el.position;
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);

  // Rotate a local-space vector (lx, ly) into world space around element origin.
  const toWorld = (lx: number, ly: number): Pt => ({
    x: x + lx * cos - ly * sin,
    y: y + lx * sin + ly * cos,
  });

  const railType = resolveRailType(el);

  if (isCurveRailType(railType)) {
    const radius = getCurveRadius(w, h);
    const sweepAngle = getCurveSweepAngle(railType);
    const sweepRad = (getCurveSweepSign(el) * sweepAngle * Math.PI) / 180;

    // p1: arc start (local angle 0 → points along +x from center)
    const p1 = toWorld(radius, 0);
    // p2: arc end (local angle = signed sweep)
    const p2 = toWorld(radius * Math.cos(sweepRad), radius * Math.sin(sweepRad));
    return [p1, p2];
  }

  if (railType === 'junction-circle') {
    const maxPorts = getCircleJunctionMaxPorts(el);
    const spokes = getCircleJunctionSpokesLocal(w, h, maxPorts);
    return spokes.map((s) => toWorld(s.tipX, s.tipY));
  }

  // H-connector: tips of both legs (home at y=0, far at y=h).
  if (railType === 'connector') {
    return [toWorld(0, 0), toWorld(w, 0), toWorld(0, h), toWorld(w, h)];
  }

  // Straight rail (and anything else): midpoints of the two short edges.
  return [toWorld(0, h / 2), toWorld(w, h / 2)];
}

/** Minimum centerline samples for curve rails (ends connect to other track). */
export function curveCenterlineNodeCount(railType: RailShapeType): number {
  if (railType === 'curve45') return 3;
  if (railType === 'curve90' || railType === 'curve180') return 4;
  return 3;
}

/**
 * Arc centerline polyline in world space. Curve45 uses 3 nodes; curve90/180 use 4.
 * First and last nodes match {@link getRailEndpoints}; interior nodes lie on the arc.
 */
export function getRailCurveCenterlineNodes(
  el: LayoutElement,
  nodeCount?: number
): Pt[] {
  const railType = resolveRailType(el);
  if (!isCurveRailType(railType)) {
    const ends = getRailEndpoints(el);
    return ends.length >= 2 ? ends : ends;
  }

  const count = Math.max(2, nodeCount ?? curveCenterlineNodeCount(railType));
  const { x, y } = el.position;
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const toWorld = (lx: number, ly: number): Pt => ({
    x: x + lx * cos - ly * sin,
    y: y + lx * sin + ly * cos,
  });

  const radius = getCurveRadius(w, h);
  const sweepRad = (getCurveSweepSign(el) * getCurveSweepAngle(railType) * Math.PI) / 180;
  const nodes: Pt[] = [];
  for (let i = 0; i < count; i++) {
    const t = count <= 1 ? 0 : i / (count - 1);
    const lx = radius * Math.cos(t * sweepRad);
    const ly = radius * Math.sin(t * sweepRad);
    nodes.push(toWorld(lx, ly));
  }
  return nodes;
}

const normRad = (rad: number): number => {
  let a = rad % (Math.PI * 2);
  if (a <= -Math.PI) a += Math.PI * 2;
  if (a > Math.PI) a -= Math.PI * 2;
  return a;
};

/** Centerline sample count for a junction turn arc (matches curve45/curve90). */
export function junctionTurnCenterlineCount(sweepDeg: number): number {
  const a = Math.abs(sweepDeg);
  if (a <= 52) return 3;
  if (a <= 135) return 4;
  return 4;
}

function parseJunctionPortIndex(raw: unknown, maxPorts: number): number | null {
  if (typeof raw !== 'string') return null;
  const match = /^port-(\d+)$/.exec(raw);
  if (!match) return null;
  const index = Number(match[1]);
  if (!Number.isInteger(index) || index < 0 || index >= maxPorts) return null;
  return index;
}

/**
 * Same 3/4-node polyline model as {@link getRailCurveCenterlineNodes}, but on a
 * circular junction: endpoints at connected spoke tips, interior samples on the
 * drivable ring between the active port pair.
 */
export function getCircleJunctionTurnCenterlineNodes(
  el: LayoutElement,
  layoutElements: LayoutElement[] = []
): Pt[] {
  if (resolveRailType(el) !== 'junction-circle') return [];

  const maxPorts = getCircleJunctionMaxPorts(el);
  const active = Array.from(getCircleJunctionActivePortIndices(el, layoutElements)).filter(
    (i) => i >= 0 && i < maxPorts
  );
  if (active.length < 2) return [];

  let portA = active[0];
  let portB = active[1];
  const connections = el.customProperties?.connections;
  if (Array.isArray(connections) && connections.length > 0) {
    const conn = connections[0] as { fromPortId?: unknown; toPortId?: unknown };
    const from = parseJunctionPortIndex(conn.fromPortId, maxPorts);
    const to = parseJunctionPortIndex(conn.toPortId, maxPorts);
    if (from !== null && to !== null && from !== to) {
      portA = from;
      portB = to;
    }
  }

  const { x, y } = el.position;
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const rot = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const toWorld = (lx: number, ly: number): Pt => ({
    x: x + lx * cos - ly * sin,
    y: y + lx * sin + ly * cos,
  });

  const spokes = getCircleJunctionSpokesLocal(w, h, maxPorts);
  const tipA = toWorld(spokes[portA]!.tipX, spokes[portA]!.tipY);
  const tipB = toWorld(spokes[portB]!.tipX, spokes[portB]!.tipY);
  const ringA = toWorld(spokes[portA]!.ringX, spokes[portA]!.ringY);
  const ringB = toWorld(spokes[portB]!.ringX, spokes[portB]!.ringY);
  const center = toWorld(w / 2, h / 2);

  const thetaA = Math.atan2(ringA.y - center.y, ringA.x - center.x);
  const thetaB = Math.atan2(ringB.y - center.y, ringB.x - center.x);
  const sweep = normRad(thetaB - thetaA);
  if (Math.abs(sweep) < (5 * Math.PI) / 180) return [];

  const sweepDeg = (Math.abs(sweep) * 180) / Math.PI;
  const count = junctionTurnCenterlineCount(sweepDeg);
  const ringR = Math.hypot(ringA.x - center.x, ringA.y - center.y);
  const nodes: Pt[] = [];

  for (let i = 0; i < count; i++) {
    if (i === 0) {
      nodes.push(tipA);
      continue;
    }
    if (i === count - 1) {
      nodes.push(tipB);
      continue;
    }
    const t = i / (count - 1);
    const th = thetaA + sweep * t;
    nodes.push({
      x: center.x + ringR * Math.cos(th),
      y: center.y + ringR * Math.sin(th),
    });
  }
  return nodes;
}

export interface ClearanceConflict {
  aId: string;
  bId: string;
}

/**
 * Flags clearance conflicts: a rail overlapping an equipment footprint, or two
 * equipment footprints sitting closer than {@link EQUIPMENT_MIN_GAP}. Rail-to-rail
 * overlaps are ignored since segments are meant to connect, and OHT vehicles are
 * allowed to ride on rails.
 */
export function checkClearance(elements: LayoutElement[]): ClearanceConflict[] {
  // Matches placement: process tools imported from DXF are equipment too, so a
  // narrow type set used to let the most common overlap through unflagged.
  const relevant = elements.filter(
    (e) => e.metadata.visible && (ALL_RAIL_TYPES.has(e.type) || isLayoutEquipmentElement(e))
  );
  const boxes = relevant.map((e) => ({
    id: e.id,
    isRail: ALL_RAIL_TYPES.has(e.type),
    box: getAABB(e),
  }));

  const conflicts: ClearanceConflict[] = [];
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i];
      const b = boxes[j];
      if (a.isRail && b.isRail) continue;
      const aBox = !a.isRail && !b.isRail ? inflateAABB(a.box, EQUIPMENT_MIN_GAP * 0.5) : a.box;
      const bBox = !a.isRail && !b.isRail ? inflateAABB(b.box, EQUIPMENT_MIN_GAP * 0.5) : b.box;
      if (aabbOverlap(aBox, bBox)) {
        conflicts.push({ aId: a.id, bId: b.id });
      }
    }
  }
  return conflicts;
}

export interface LoopResult {
  openEndpoints: Pt[];
  railCount: number;
  nodeCount: number;
  loops: number; // cyclomatic number (independent cycles)
  closed: boolean; // at least one loop and no open ends
}

/**
 * Builds a graph from rail endpoints (clustered by proximity) and reports open
 * ends (degree < 2) plus the number of independent loops. A topology is
 * considered closed when there is at least one rail, no open ends, and at least
 * one loop — i.e. a vehicle cannot be stranded.
 */
export function validateLoopClosure(
  elements: LayoutElement[],
  tolerance: number = VALIDATION_ENDPOINT_TOLERANCE
): LoopResult {
  const rails = elements.filter(
    (e) => e.metadata.visible && ALL_RAIL_TYPES.has(e.type)
  );

  const nodes: Pt[] = [];
  const degree: number[] = [];
  const tol2 = tolerance * tolerance;

  const findNode = (p: Pt): number => {
    for (let i = 0; i < nodes.length; i++) {
      const dx = nodes[i].x - p.x;
      const dy = nodes[i].y - p.y;
      if (dx * dx + dy * dy <= tol2) return i;
    }
    nodes.push({ ...p });
    degree.push(0);
    return nodes.length - 1;
  };

  const edges: Array<[number, number]> = [];
  rails.forEach((rail) => {
    const pts = getRailEndpoints(rail);
    const railType = resolveRailType(rail);
    if (railType === 'junction-circle') {
      const connections = (rail.customProperties?.connections as any[]) || [];
      connections.forEach((conn) => {
        const fromIdx = Number(conn.fromPortId.replace('port-', ''));
        const toIdx = Number(conn.toPortId.replace('port-', ''));
        if (
          Number.isFinite(fromIdx) &&
          fromIdx >= 0 &&
          fromIdx < pts.length &&
          Number.isFinite(toIdx) &&
          toIdx >= 0 &&
          toIdx < pts.length
        ) {
          const na = findNode(pts[fromIdx]);
          const nb = findNode(pts[toIdx]);
          degree[na] += 1;
          degree[nb] += 1;
          edges.push([na, nb]);
        }
      });
    } else if (pts.length >= 2) {
      // Straight / curve: connect the two endpoints.
      const na = findNode(pts[0]);
      const nb = findNode(pts[1]);
      degree[na] += 1;
      degree[nb] += 1;
      edges.push([na, nb]);
    }
  });

  // Union-find for connected components
  const parent = nodes.map((_, i) => i);
  const find = (x: number): number => {
    let root = x;
    while (parent[root] !== root) root = parent[root];
    while (parent[x] !== root) {
      const next = parent[x];
      parent[x] = root;
      x = next;
    }
    return root;
  };
  const union = (a: number, b: number) => {
    parent[find(a)] = find(b);
  };
  edges.forEach(([a, b]) => union(a, b));

  const componentRoots = new Set(nodes.map((_, i) => find(i)));
  const N = nodes.length;
  const E = edges.length;
  const C = componentRoots.size;
  const loops = N === 0 ? 0 : E - N + C;

  const openEndpoints = nodes.filter((_, i) => degree[i] < 2);
  const closed = rails.length > 0 && openEndpoints.length === 0 && loops >= 1;

  return { openEndpoints, railCount: rails.length, nodeCount: N, loops, closed };
}

// ─── OHT lane snapping ────────────────────────────────────────────────────────

/** Standard thickness for all rail types on the canvas. */
export const RAIL_THICKNESS = 4;

export function getTrackStrokeWidth(): number {
  // Return standard thickness (4) for all rail segments to ensure visual consistency
  // across straight, curve, and junction types regardless of bounding box height.
  return RAIL_THICKNESS;
}

export function getCurveRadius(width: number, height: number): number {
  return Math.max(0.001, Math.min(width, height));
}

function localToWorld(
  tx: number,
  ty: number,
  cos: number,
  sin: number,
  localX: number,
  localY: number
): Pt {
  return {
    x: tx + localX * cos - localY * sin,
    y: ty + localX * sin + localY * cos,
  };
}

/** How close (px) an OHT centre must be to a lane before it snaps. */
export const OHT_SNAP_TOLERANCE = 28;

/** Signed shortest difference between two headings, in (-180, 180]. */
function signedHeadingDeltaDeg(a: number, b: number): number {
  return ((((a - b) % 360) + 540) % 360) - 180;
}

/**
 * Nearest visible rail under an OHT (within {@link OHT_SNAP_TOLERANCE}), plus the
 * rotation that lays the vehicle along that rail and the top-left position that
 * centres it on the lane at that rotation.
 *
 * Bidirectional rails keep whichever way the vehicle already faces; a
 * unidirectional rail always faces its travel heading.
 */
export function findNearestRailForOHT(
  oht: LayoutElement,
  rails: LayoutElement[]
): { rail: LayoutElement; position: { x: number; y: number }; rotation: number } | null {
  const ohtW = oht.dimensions.width;
  const ohtH = oht.dimensions.height;
  const { pivotX, pivotY } = getOhtPivot(oht, ohtW, ohtH);
  const { x: cx, y: cy } = getOhtPivotWorld(oht, ohtW, ohtH);

  let bestDist = Infinity;
  let best: {
    rail: LayoutElement;
    position: { x: number; y: number };
    rotation: number;
  } | null = null;

  for (const rail of rails) {
    if (!ALL_RAIL_TYPES.has(rail.type) || !rail.metadata.visible) continue;

    const { x: tx, y: ty } = rail.position;
    const rotRad = ((rail.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rotRad);
    const sin = Math.sin(rotRad);

    const ddx = cx - tx;
    const ddy = cy - ty;
    const localX = ddx * cos + ddy * sin;
    const localY = -ddx * sin + ddy * cos;

    const railType = resolveRailType(rail);
    let snapLocal: { x: number; y: number };
    // Bearing of the lane at the snap point, in the rail's own frame.
    let tangentLocalDeg: number;

    if (isCurveRailType(railType)) {
      const radius = getCurveRadius(rail.dimensions.width, rail.dimensions.height);
      const sweepSign = getCurveSweepSign(rail);
      const sweepDeg = sweepSign * getCurveSweepAngle(railType);
      const sweepMin = Math.min(0, sweepDeg);
      const sweepMax = Math.max(0, sweepDeg);
      const polarDeg = Math.atan2(localY, localX) * (180 / Math.PI);
      if (polarDeg < sweepMin - 4 || polarDeg > sweepMax + 4) continue;
      const clampedDeg = Math.max(sweepMin, Math.min(sweepMax, polarDeg));
      const clampedRad = (clampedDeg * Math.PI) / 180;
      snapLocal = {
        x: radius * Math.cos(clampedRad),
        y: radius * Math.sin(clampedRad),
      };
      tangentLocalDeg = clampedDeg + 90 * sweepSign;
    } else {
      const { width: tw, height: th } = rail.dimensions;
      const halfOHT = Math.min(ohtW, ohtH) / 2;
      if (localX < -halfOHT * 2 || localX > tw + halfOHT * 2) continue;
      const clampedX = Math.max(halfOHT, Math.min(tw - halfOHT, localX));
      snapLocal = { x: clampedX, y: th / 2 };
      tangentLocalDeg = 0;
    }

    const world = localToWorld(tx, ty, cos, sin, snapLocal.x, snapLocal.y);
    const dist = Math.hypot(cx - world.x, cy - world.y);
    if (dist >= bestDist) continue;

    const travelDeg = normalizeDeg((rail.rotation || 0) + tangentLocalDeg);
    const keepsFacing =
      !isTrackUnidirectional(rail) &&
      Math.abs(signedHeadingDeltaDeg(travelDeg, oht.rotation || 0)) > 90;
    const rotation = keepsFacing ? normalizeDeg(travelDeg + 180) : travelDeg;

    // Konva turns the vehicle about its top-left, so back the pivot out through
    // the snapped rotation to leave the body centred on the lane.
    const faceRad = (rotation * Math.PI) / 180;
    const faceCos = Math.cos(faceRad);
    const faceSin = Math.sin(faceRad);

    bestDist = dist;
    best = {
      rail,
      position: {
        x: world.x - (pivotX * faceCos - pivotY * faceSin),
        y: world.y - (pivotX * faceSin + pivotY * faceCos),
      },
      rotation,
    };
  }

  return bestDist <= OHT_SNAP_TOLERANCE ? best : null;
}

/**
 * Seat an OHT on the nearest lane centre, squared up with the track.
 * @param oht - OHT element at proposed position
 * @param rails - Array of rail elements to snap to
 * @returns Top-left position and rail-aligned rotation, or null if no lane is
 *   within {@link OHT_SNAP_TOLERANCE}
 */
export function findOHTLaneSnapPosition(
  oht: LayoutElement,
  rails: LayoutElement[]
): { x: number; y: number; rotation: number } | null {
  const nearest = findNearestRailForOHT(oht, rails);
  return nearest ? { ...nearest.position, rotation: nearest.rotation } : null;
}

/**
 * Find translation offset to snap rail endpoints to nearby rails.
 * Searches for the nearest endpoint-to-endpoint pair between the moved rail and others
 * and returns the translation offset needed for a snap.
 * @param moved - Rail element being moved
 * @param others - Array of other rail elements to snap to
 * @param tolerance - Maximum distance for snapping (default: ENDPOINT_TOLERANCE + 4)
 * @returns Translation offset {dx, dy} for snapping, or null if no snap within tolerance
 */
export function findRailSnapOffset(
  moved: LayoutElement,
  others: LayoutElement[],
  tolerance: number = ENDPOINT_TOLERANCE + 4
): { dx: number; dy: number } | null {
  const myEnds = getRailEndpoints(moved);
  let best: { dx: number; dy: number; dist: number } | null = null;

  for (const other of others) {
    if (other.id === moved.id || !ALL_RAIL_TYPES.has(other.type) || !other.metadata.visible) continue;
    const otherEnds = getRailEndpoints(other);
    for (const me of myEnds) {
      for (const oe of otherEnds) {
        const dx = oe.x - me.x;
        const dy = oe.y - me.y;
        const dist = Math.hypot(dx, dy);
        if (dist <= tolerance && (!best || dist < best.dist)) {
          best = { dx, dy, dist };
        }
      }
    }
  }

  return best ? { dx: best.dx, dy: best.dy } : null;
}

const translateAABB = (box: AABB, dx: number, dy: number): AABB => ({
  minX: box.minX + dx,
  minY: box.minY + dy,
  maxX: box.maxX + dx,
  maxY: box.maxY + dy,
});

const rangeOverlap = (aMin: number, aMax: number, bMin: number, bMax: number): number =>
  Math.min(aMax, bMax) - Math.max(aMin, bMin);

/**
 * Returns the smallest offset that snaps a movable object's AABB edge to the
 * nearest visible non-rail object's edge when within tolerance.
 */
export function findObjectEdgeSnapOffset(
  moved: LayoutElement,
  others: LayoutElement[],
  tolerance: number = OBJECT_EDGE_SNAP_TOLERANCE
): { dx: number; dy: number } | null {
  const movedBox = getAABB(moved);
  let best: { dx: number; dy: number; dist: number } | null = null;

  for (const other of others) {
    if (other.id === moved.id || !other.metadata.visible || ALL_RAIL_TYPES.has(other.type)) continue;
    const otherBox = getAABB(other);

    const yOverlap = rangeOverlap(movedBox.minY, movedBox.maxY, otherBox.minY, otherBox.maxY);
    if (yOverlap > 0) {
      const toOtherLeft = otherBox.minX - movedBox.maxX;
      if (Math.abs(toOtherLeft) <= tolerance && (!best || Math.abs(toOtherLeft) < best.dist)) {
        best = { dx: toOtherLeft, dy: 0, dist: Math.abs(toOtherLeft) };
      }

      const toOtherRight = otherBox.maxX - movedBox.minX;
      if (Math.abs(toOtherRight) <= tolerance && (!best || Math.abs(toOtherRight) < best.dist)) {
        best = { dx: toOtherRight, dy: 0, dist: Math.abs(toOtherRight) };
      }
    }

    const xOverlap = rangeOverlap(movedBox.minX, movedBox.maxX, otherBox.minX, otherBox.maxX);
    if (xOverlap > 0) {
      const toOtherTop = otherBox.minY - movedBox.maxY;
      if (Math.abs(toOtherTop) <= tolerance && (!best || Math.abs(toOtherTop) < best.dist)) {
        best = { dx: 0, dy: toOtherTop, dist: Math.abs(toOtherTop) };
      }

      const toOtherBottom = otherBox.maxY - movedBox.minY;
      if (
        Math.abs(toOtherBottom) <= tolerance &&
        (!best || Math.abs(toOtherBottom) < best.dist)
      ) {
        best = { dx: 0, dy: toOtherBottom, dist: Math.abs(toOtherBottom) };
      }
    }
  }

  return best ? { dx: best.dx, dy: best.dy } : null;
}

function inflateAABB(box: AABB, by: number): AABB {
  return {
    minX: box.minX - by,
    minY: box.minY - by,
    maxX: box.maxX + by,
    maxY: box.maxY + by,
  };
}

export type OverlapResolveOptions = {
  /**
   * Clearance to leave between the moved element and each equipment neighbour.
   * Only applies between two pieces of equipment — anything else may still sit
   * flush, so walls and zones keep snapping tight to a tool.
   */
  minGap?: number;
  /** Restrict the push to one world axis, e.g. to slide along a rail. */
  axis?: 'x' | 'y';
};

/**
 * Pushes a movable object out of intersections with visible non-rail objects.
 * Returned offset keeps edges touching, or {@link OverlapResolveOptions.minGap}
 * apart, instead of letting footprints stack.
 */
export function resolveObjectOverlapOffset(
  moved: LayoutElement,
  others: LayoutElement[],
  options: OverlapResolveOptions = {}
): { dx: number; dy: number } {
  const base = getAABB(moved);
  const minGap = Math.max(0, options.minGap ?? 0);
  const movedIsEquipment = minGap > 0 && isLayoutEquipmentElement(moved);
  let totalDx = 0;
  let totalDy = 0;

  for (let i = 0; i < Math.max(4, others.length + 1); i++) {
    const current = translateAABB(base, totalDx, totalDy);
    let bestResolve: { dx: number; dy: number; dist: number } | null = null;

    for (const other of others) {
      if (other.id === moved.id || !other.metadata.visible || ALL_RAIL_TYPES.has(other.type)) continue;
      const gap = movedIsEquipment && isLayoutEquipmentElement(other) ? minGap : 0;
      const otherBox = gap > 0 ? inflateAABB(getAABB(other), gap) : getAABB(other);
      if (!aabbOverlap(current, otherBox)) continue;

      const candidates = [
        { dx: otherBox.minX - current.maxX, dy: 0 },
        { dx: otherBox.maxX - current.minX, dy: 0 },
        { dx: 0, dy: otherBox.minY - current.maxY },
        { dx: 0, dy: otherBox.maxY - current.minY },
      ].filter((c) => (options.axis === 'x' ? c.dy === 0 : options.axis === 'y' ? c.dx === 0 : true));

      for (const c of candidates) {
        const dist = Math.hypot(c.dx, c.dy);
        if (!bestResolve || dist < bestResolve.dist) {
          bestResolve = { ...c, dist };
        }
      }
    }

    if (!bestResolve) break;
    totalDx += bestResolve.dx;
    totalDy += bestResolve.dy;
  }

  return { dx: totalDx, dy: totalDy };
}

/**
 * World axis the element's dock line runs along, so a tool can be shuffled
 * sideways to make room without being pushed off the rail it docks to.
 */
function dockLineAxis(el: LayoutElement): 'x' | 'y' {
  const rot = normalizeDeg(el.rotation || 0);
  const alongY = (rot > 45 && rot < 135) || (rot > 225 && rot < 315);
  return alongY ? 'y' : 'x';
}

/**
 * Final placement guard for equipment: nudges a tool clear of its neighbours and
 * keeps {@link EQUIPMENT_MIN_GAP} between footprints. Unlike
 * {@link getObjectEdgeSnapPosition} this is not a snapping convenience, so it
 * runs whether or not the user has snap-to-objects on — two tools sharing floor
 * space is an invalid layout, not an untidy one.
 */
export function getEquipmentSpacingPosition(
  moved: LayoutElement,
  others: LayoutElement[],
  options: { minGap?: number; alongDockLine?: boolean } = {}
): { x: number; y: number } {
  if (!moved.metadata.visible || !isLayoutEquipmentElement(moved)) {
    return { x: moved.position.x, y: moved.position.y };
  }
  const { dx, dy } = resolveObjectOverlapOffset(moved, others, {
    minGap: options.minGap ?? EQUIPMENT_MIN_GAP,
    axis: options.alongDockLine ? dockLineAxis(moved) : undefined,
  });
  return { x: moved.position.x + dx, y: moved.position.y + dy };
}

/**
 * Computes final top-left for an object so it snaps to nearby edges and never
 * ends overlapped with another visible non-rail object.
 */
export function getObjectEdgeSnapPosition(
  moved: LayoutElement,
  others: LayoutElement[],
  tolerance: number = OBJECT_EDGE_SNAP_TOLERANCE
): { x: number; y: number } {
  const edgeOffset = findObjectEdgeSnapOffset(moved, others, tolerance) ?? { dx: 0, dy: 0 };
  const edgeSnapped: LayoutElement = {
    ...moved,
    position: {
      x: moved.position.x + edgeOffset.dx,
      y: moved.position.y + edgeOffset.dy,
    },
  };
  const overlapOffset = resolveObjectOverlapOffset(edgeSnapped, others);

  return {
    x: edgeSnapped.position.x + overlapOffset.dx,
    y: edgeSnapped.position.y + overlapOffset.dy,
  };
}

/**
 * Angle between two rail travel axes in [0, 90] (parallel ⇒ ~0).
 */
function railAxisAngleDeg(a: LayoutElement, b: LayoutElement): number {
  const [a0, a1] = getRailEndpoints(a);
  const [b0, b1] = getRailEndpoints(b);
  const adx = a1.x - a0.x;
  const ady = a1.y - a0.y;
  const bdx = b1.x - b0.x;
  const bdy = b1.y - b0.y;
  const al = Math.hypot(adx, ady);
  const bl = Math.hypot(bdx, bdy);
  if (al < 1e-9 || bl < 1e-9) return 90;
  const cos = Math.abs((adx * bdx + ady * bdy) / (al * bl));
  return (Math.acos(Math.min(1, Math.max(0, cos))) * 180) / Math.PI;
}

/** Signed lateral distance of point P from the infinite line through A→B. */
function lateralDistanceToSegmentAxis(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return Math.hypot(p.x - a.x, p.y - a.y);
  return (dx * (p.y - a.y) - dy * (p.x - a.x)) / len;
}

/** Along-track overlap length of two collinear segments (0 if separated). */
function alongTrackOverlapLength(a0: Pt, a1: Pt, b0: Pt, b1: Pt): number {
  const dx = a1.x - a0.x;
  const dy = a1.y - a0.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return 0;
  const ux = dx / len;
  const uy = dy / len;
  const proj = (p: Pt) => (p.x - a0.x) * ux + (p.y - a0.y) * uy;
  const aMin = 0;
  const aMax = len;
  const bMin = Math.min(proj(b0), proj(b1));
  const bMax = Math.max(proj(b0), proj(b1));
  return Math.max(0, Math.min(aMax, bMax) - Math.max(aMin, bMin));
}

/**
 * True when two straight rails share the same centerline and overlap along it
 * (stacked on top of each other rather than end-to-end connected).
 */
export function railsAreCollinearStacked(
  a: LayoutElement,
  b: LayoutElement,
  options?: { lateralTol?: number; minOverlap?: number }
): boolean {
  if (resolveRailType(a) !== 'straight' || resolveRailType(b) !== 'straight') return false;
  if (railAxisAngleDeg(a, b) > 8) return false;

  const [a0, a1] = getRailEndpoints(a);
  const [b0, b1] = getRailEndpoints(b);
  const lateralTol =
    options?.lateralTol ??
    Math.max(ENDPOINT_TOLERANCE + 2, Math.min(a.dimensions.height, b.dimensions.height) * 0.75);
  const midB = { x: (b0.x + b1.x) / 2, y: (b0.y + b1.y) / 2 };
  if (Math.abs(lateralDistanceToSegmentAxis(midB, a0, a1)) > lateralTol) return false;

  const overlap = alongTrackOverlapLength(a0, a1, b0, b1);
  const minOverlap =
    options?.minOverlap ??
    Math.min(Math.hypot(a1.x - a0.x, a1.y - a0.y), Math.hypot(b1.x - b0.x, b1.y - b0.y)) * 0.2;
  return overlap >= Math.max(ENDPOINT_TOLERANCE, minOverlap);
}

/**
 * Translate `moved` so `fromTip` lands on `toTip` (same rotation preserved).
 */
function translateRailTipTo(moved: LayoutElement, fromTip: Pt, toTip: Pt): { x: number; y: number } {
  return {
    x: moved.position.x + (toTip.x - fromTip.x),
    y: moved.position.y + (toTip.y - fromTip.y),
  };
}

/**
 * Place `moved` extending outward from `hostEnd` along `outward`, attaching the
 * nearer tip of `moved` to that host end.
 */
function extendRailFromHostEnd(
  moved: LayoutElement,
  hostEnd: Pt,
  outward: Pt
): { x: number; y: number } {
  const [m0, m1] = getRailEndpoints(moved);
  const ox = outward.x;
  const oy = outward.y;
  // Choose which moved tip attaches so the other tip continues in +outward.
  const attachM0 = {
    pos: translateRailTipTo(moved, m0, hostEnd),
    score: (m1.x - m0.x) * ox + (m1.y - m0.y) * oy,
  };
  const attachM1 = {
    pos: translateRailTipTo(moved, m1, hostEnd),
    score: (m0.x - m1.x) * ox + (m0.y - m1.y) * oy,
  };
  const best = attachM0.score >= attachM1.score ? attachM0 : attachM1;
  return best.pos;
}

export type RailDropResolve = { x: number; y: number; rotation?: number };

/** True when drop anchor sits on (or very near) another straight rail's centerline. */
function isSameLineDropCandidate(
  moved: LayoutElement,
  host: LayoutElement,
  anchor: Pt
): boolean {
  if (resolveRailType(host) !== 'straight') return false;
  if (railAxisAngleDeg(moved, host) > 8) return false;
  if (railsAreCollinearStacked(moved, host)) return true;

  const [h0, h1] = getRailEndpoints(host);
  const thickness = Math.max(host.dimensions.height, moved.dimensions.height, 2);
  const lat = Math.abs(lateralDistanceToSegmentAxis(anchor, h0, h1));
  if (lat > thickness * 1.5) return false;

  const dx = h1.x - h0.x;
  const dy = h1.y - h0.y;
  const lenSq = dx * dx + dy * dy || 1;
  const t = ((anchor.x - h0.x) * dx + (anchor.y - h0.y) * dy) / lenSq;
  return t >= -0.2 && t <= 1.2;
}

function placeRailAdjacentToHost(
  moved: LayoutElement,
  host: LayoutElement,
  anchor: Pt
): RailDropResolve {
  const aligned: LayoutElement = { ...moved, rotation: host.rotation };
  const [h0, h1] = getRailEndpoints(host);
  const hostLen = Math.hypot(h1.x - h0.x, h1.y - h0.y) || 1;
  const dir = { x: (h1.x - h0.x) / hostLen, y: (h1.y - h0.y) / hostLen };
  const lateral = { x: -dir.y, y: dir.x };
  const signedLat = lateralDistanceToSegmentAxis(anchor, h0, h1);
  const side = signedLat >= 0 ? 1 : -1;
  const gap = Math.max(aligned.dimensions.height, host.dimensions.height) * 1.05;

  const dx = h1.x - h0.x;
  const dy = h1.y - h0.y;
  const t = Math.max(
    0,
    Math.min(1, ((anchor.x - h0.x) * dx + (anchor.y - h0.y) * dy) / (hostLen * hostLen))
  );
  const onAxis = { x: h0.x + dx * t, y: h0.y + dy * t };
  const targetMid = {
    x: onAxis.x + lateral.x * side * gap,
    y: onAxis.y + lateral.y * side * gap,
  };

  const [m0, m1] = getRailEndpoints(aligned);
  const curMid = { x: (m0.x + m1.x) / 2, y: (m0.y + m1.y) / 2 };
  return {
    x: aligned.position.x + (targetMid.x - curMid.x),
    y: aligned.position.y + (targetMid.y - curMid.y),
    rotation: host.rotation,
  };
}

/**
 * Resolve a straight-rail drop that would stack on an existing centerline:
 * - near a host endpoint → extend end-to-end
 * - otherwise → shift to an adjacent parallel lane (never stay on the same line)
 */
export function resolveStackedOrCoincidentRailDrop(
  moved: LayoutElement,
  others: LayoutElement[],
  options?: { preferredAnchor?: Pt; endpointExtendTol?: number }
): RailDropResolve | null {
  if (resolveRailType(moved) !== 'straight') return null;

  const anchor = options?.preferredAnchor ?? {
    x: moved.position.x + moved.dimensions.width / 2,
    y: moved.position.y + moved.dimensions.height / 2,
  };
  const extendTol = options?.endpointExtendTol ?? Math.max(SNAP_DRAG_TOLERANCE, 36);

  let bestHost: LayoutElement | null = null;
  let bestScore = Infinity;

  for (const other of others) {
    if (other.id === moved.id || !other.metadata.visible) continue;
    if (!ALL_RAIL_TYPES.has(other.type) || resolveRailType(other) !== 'straight') continue;
    if (!isSameLineDropCandidate(moved, other, anchor)) continue;

    const [h0, h1] = getRailEndpoints(other);
    const mid = { x: (h0.x + h1.x) / 2, y: (h0.y + h1.y) / 2 };
    const lat = Math.abs(lateralDistanceToSegmentAxis(anchor, h0, h1));
    const score = lat * 8 + Math.hypot(anchor.x - mid.x, anchor.y - mid.y);
    if (score < bestScore) {
      bestScore = score;
      bestHost = other;
    }
  }

  if (!bestHost) return null;

  const host = bestHost;
  const [h0, h1] = getRailEndpoints(host);
  const hostLen = Math.hypot(h1.x - h0.x, h1.y - h0.y) || 1;
  const dir = { x: (h1.x - h0.x) / hostLen, y: (h1.y - h0.y) / hostLen };

  const d0 = Math.hypot(anchor.x - h0.x, anchor.y - h0.y);
  const d1 = Math.hypot(anchor.x - h1.x, anchor.y - h1.y);
  const nearEndDist = Math.min(d0, d1);
  // Mid-span coincident drops should go adjacent; only extend when clearly near a tip.
  const preferExtend = nearEndDist <= Math.min(extendTol, hostLen * 0.35);

  if (preferExtend) {
    const atH1 = d1 <= d0;
    const hostEnd = atH1 ? h1 : h0;
    const outward = atH1 ? dir : { x: -dir.x, y: -dir.y };
    const aligned: LayoutElement = { ...moved, rotation: host.rotation };
    const extended = extendRailFromHostEnd(aligned, hostEnd, outward);
    const placed: LayoutElement = { ...aligned, position: extended };
    if (!railsAreCollinearStacked(placed, host, { minOverlap: moved.dimensions.width * 0.45 })) {
      return { ...extended, rotation: host.rotation };
    }
  }

  return placeRailAdjacentToHost(moved, host, anchor);
}

/**
 * For dropped rails, resolves rail-on-rail stacking by shifting the new rail
 * to the nearest adjacent non-overlapping position.
 *
 * When the drop overlaps another rail, prefer the side of `preferredAnchor`
 * (usually the raw pointer / pre-snap position) so a left-side drop stays left
 * of a vertical host instead of flipping to the shorter right-side push.
 *
 * Same-line / coincident straight drops extend from an end or park on an
 * adjacent parallel lane — they never remain stacked on the host centerline.
 */
export function getRailNonOverlappingDropPosition(
  moved: LayoutElement,
  others: LayoutElement[],
  options?: { preferredAnchor?: { x: number; y: number } }
): RailDropResolve {
  if (!ALL_RAIL_TYPES.has(moved.type)) {
    return { x: moved.position.x, y: moved.position.y };
  }

  const stacked = resolveStackedOrCoincidentRailDrop(moved, others, {
    preferredAnchor: options?.preferredAnchor,
  });
  if (stacked) {
    const resolved: LayoutElement = {
      ...moved,
      position: { x: stacked.x, y: stacked.y },
      rotation: stacked.rotation ?? moved.rotation,
    };
    const separated = separateOverlappingRails(resolved, others, options);
    return {
      x: separated.x,
      y: separated.y,
      rotation: stacked.rotation,
    };
  }

  return separateOverlappingRails(moved, others, options);
}

function separateOverlappingRails(
  moved: LayoutElement,
  others: LayoutElement[],
  options?: { preferredAnchor?: { x: number; y: number } }
): { x: number; y: number } {
  // If an endpoint is already snapped to another rail endpoint, preserve that
  // connection — unless the rails are stacked on the same centerline.
  const movedEnds = getRailEndpoints(moved);
  const keepConnected = others.some((other) => {
    if (other.id === moved.id || !other.metadata.visible || !ALL_RAIL_TYPES.has(other.type)) {
      return false;
    }
    if (railsAreCollinearStacked(moved, other)) return false;
    const otherEnds = getRailEndpoints(other);
    return movedEnds.some((me) =>
      otherEnds.some((oe) => Math.hypot(oe.x - me.x, oe.y - me.y) <= ENDPOINT_TOLERANCE + 4)
    );
  });
  if (keepConnected) {
    return { x: moved.position.x, y: moved.position.y };
  }

  // Junctions intentionally intersect branch geometry around a shared hub.
  if (isJunctionRailType(resolveRailType(moved))) {
    return { x: moved.position.x, y: moved.position.y };
  }

  // H-connectors intentionally overlap both parallel host rails.
  if (resolveRailType(moved) === 'connector') {
    return { x: moved.position.x, y: moved.position.y };
  }

  const anchor = options?.preferredAnchor ?? moved.position;
  let currentX = moved.position.x;
  let currentY = moved.position.y;

  for (let i = 0; i < Math.max(4, others.length + 1); i++) {
    const currentEl: LayoutElement = {
      ...moved,
      position: { x: currentX, y: currentY },
    };
    const currentBox = getAABB(currentEl);

    let bestResolve: { dx: number; dy: number; rank: number; dist: number } | null = null;

    for (const other of others) {
      if (other.id === moved.id || !other.metadata.visible || !ALL_RAIL_TYPES.has(other.type)) continue;
      if (isJunctionRailType(resolveRailType(other))) continue;
      const otherBox = getAABB(other);
      if (!aabbOverlap(currentBox, otherBox)) continue;

      const otherCx = (otherBox.minX + otherBox.maxX) / 2;
      const otherCy = (otherBox.minY + otherBox.maxY) / 2;
      const preferSignX = Math.sign(anchor.x - otherCx);
      const preferSignY = Math.sign(anchor.y - otherCy);
      const overlapX = rangeOverlap(currentBox.minX, currentBox.maxX, otherBox.minX, otherBox.maxX);
      const overlapY = rangeOverlap(currentBox.minY, currentBox.maxY, otherBox.minY, otherBox.maxY);
      // Parallel vertical hosts share a long Y overlap → separate sideways (X).
      // Parallel horizontal hosts share a long X overlap → separate along Y.
      const preferHorizontalSep = overlapY >= overlapX;

      const candidates = [
        { dx: otherBox.minX - currentBox.maxX, dy: 0 },
        { dx: otherBox.maxX - currentBox.minX, dy: 0 },
        { dx: 0, dy: otherBox.minY - currentBox.maxY },
        { dx: 0, dy: otherBox.maxY - currentBox.minY },
      ];

      for (const c of candidates) {
        const dist = Math.hypot(c.dx, c.dy);
        if (dist <= 1e-9) continue;
        const isX = Math.abs(c.dy) <= 1e-9;
        const sign = isX ? Math.sign(c.dx) : Math.sign(c.dy);
        const axisMatch = preferHorizontalSep ? isX : !isX;
        const sideMatch = isX
          ? preferSignX === 0 || sign === preferSignX
          : preferSignY === 0 || sign === preferSignY;
        // Lower rank is better: matching axis+side, then matching axis, then distance.
        const rank = (axisMatch ? 0 : 4) + (sideMatch ? 0 : 2);
        if (
          !bestResolve ||
          rank < bestResolve.rank ||
          (rank === bestResolve.rank && dist < bestResolve.dist)
        ) {
          bestResolve = { ...c, rank, dist };
        }
      }
    }

    if (!bestResolve) break;
    currentX += bestResolve.dx;
    currentY += bestResolve.dy;
  }

  return { x: currentX, y: currentY };
}

interface RailStopSegment {
  points: Pt[];
  cumStart: number;
  length: number;
}

function getRailStopSegments(rail: LayoutElement): RailStopSegment[] {
  const { x, y } = rail.position;
  const { width: w, height: h } = rail.dimensions;
  const rot = ((rail.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const toWorld = (localX: number, localY: number) => localToWorld(x, y, cos, sin, localX, localY);
  const railType = resolveRailType(rail);

  if (isCurveRailType(railType)) {
    const radius = getCurveRadius(w, h);
    const sweep = getCurveSweepSign(rail) * getCurveSweepAngle(railType);
    const segments = Math.max(8, Math.ceil(Math.abs(sweep) / 5));
    const points: Pt[] = [];

    for (let i = 0; i <= segments; i++) {
      const rad = ((sweep * i) / segments) * (Math.PI / 180);
      points.push(toWorld(radius * Math.cos(rad), radius * Math.sin(rad)));
    }

    return [{ points, cumStart: 0, length: pathLength(points) }];
  }

  if (railType === 'junction-circle') {
    const hub = toWorld(w / 2, h / 2);
    const maxPorts = getCircleJunctionMaxPorts(rail);
    const spokes = getCircleJunctionSpokesLocal(w, h, maxPorts);
    const segments: RailStopSegment[] = [];
    let cum = 0;
    for (const s of spokes) {
      const tip = toWorld(s.tipX, s.tipY);
      const points = [tip, hub];
      const length = pathLength(points);
      segments.push({ points, cumStart: cum, length });
      cum += length;
    }
    return segments;
  }

  // H-connector: home leg, crossbar, far leg (transfer path).
  if (railType === 'connector') {
    const home = [toWorld(0, 0), toWorld(w, 0)];
    const cross = [toWorld(w / 2, 0), toWorld(w / 2, h)];
    const far = [toWorld(0, h), toWorld(w, h)];
    const homeLen = pathLength(home);
    const crossLen = pathLength(cross);
    const farLen = pathLength(far);
    return [
      { points: home, cumStart: 0, length: homeLen },
      { points: cross, cumStart: homeLen, length: crossLen },
      { points: far, cumStart: homeLen + crossLen, length: farLen },
    ];
  }

  const points = [toWorld(0, h / 2), toWorld(w, h / 2)];
  return [{ points, cumStart: 0, length: pathLength(points) }];
}

function getRailStopPath(rail: LayoutElement): Pt[] {
  const segments = getRailStopSegments(rail);
  if (segments.length === 0) return [];

  const path: Pt[] = [];
  for (const seg of segments) {
    const pts = seg.points;
    if (pts.length === 0) continue;
    if (path.length === 0) {
      path.push(...pts);
      continue;
    }
    const first = pts[0];
    const last = path[path.length - 1];
    const contiguous = Math.hypot(last.x - first.x, last.y - first.y) < 1e-6;
    path.push(...(contiguous ? pts.slice(1) : pts));
  }
  return path;
}

function pathLength(points: Pt[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return total;
}

/**
 * Maps an arc-length distance along the rail's rendered centreline back to a
 * world-space point. This matches both the stop-placement preview and the OHT
 * simulation graph.
 */
export function railStopToWorld(rail: LayoutElement, distance: number): Pt {
  const segments = getRailStopSegments(rail);
  if (segments.length === 0) return { x: rail.position.x, y: rail.position.y };

  const totalLength = segments[segments.length - 1].cumStart + segments[segments.length - 1].length;
  const clamped = Math.max(0, Math.min(totalLength, distance));
  const seg =
    segments.find(
      (s) => clamped >= s.cumStart - 0.001 && clamped <= s.cumStart + s.length + 0.001
    ) || segments[segments.length - 1];

  const points = seg.points;
  if (points.length === 0) return { x: rail.position.x, y: rail.position.y };
  if (points.length === 1) return points[0];

  const local = Math.max(0, Math.min(seg.length, clamped - seg.cumStart));
  let traversed = 0;
  for (let i = 1; i < points.length; i++) {
    const p0 = points[i - 1];
    const p1 = points[i];
    const segLen = Math.hypot(p1.x - p0.x, p1.y - p0.y);
    if (traversed + segLen >= local) {
      const t = segLen > 0 ? (local - traversed) / segLen : 0;
      return {
        x: p0.x + (p1.x - p0.x) * t,
        y: p0.y + (p1.y - p0.y) * t,
      };
    }
    traversed += segLen;
  }

  return points[points.length - 1];
}

export interface RailStopProjection {
  railId: string;
  /** Arc-length distance along the rail centreline. */
  distance: number;
  /** World-space position of the projected point. */
  x: number;
  y: number;
  /** Distance from the query point to the rail centreline. */
  perpDist: number;
}

/**
 * Projects a world-space point onto the nearest rail's main axis and returns the
 * snapped position plus the distance-along-rail, or null if no rail is within
 * `maxPerpDist`. Shared by the stop-placement click handler and its live
 * preview so both land on exactly the same spot.
 */
export function projectPointToNearestRail(
  rails: LayoutElement[],
  p: Pt,
  maxPerpDist: number = Infinity
): RailStopProjection | null {
  let best: RailStopProjection | null = null;

  for (const rail of rails) {
    if (!ALL_RAIL_TYPES.has(rail.type) || !rail.metadata.visible) continue;

    const railSegments = getRailStopSegments(rail);

    for (const segment of railSegments) {
      const points = segment.points;
      let cumulative = 0;

      for (let i = 1; i < points.length; i++) {
        const a = points[i - 1];
        const b = points[i];
        const vx = b.x - a.x;
        const vy = b.y - a.y;
        const segLen = Math.hypot(vx, vy);
        if (segLen <= 0) continue;

        const t = Math.max(
          0,
          Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / (segLen * segLen))
        );
        const x = a.x + vx * t;
        const y = a.y + vy * t;
        const perpDist = Math.hypot(p.x - x, p.y - y);
        const distance = segment.cumStart + cumulative + segLen * t;

        if (!best || perpDist < best.perpDist) {
          best = { railId: rail.id, distance, x, y, perpDist };
        }

        cumulative += segLen;
      }
    }
  }

  return best && best.perpDist <= maxPerpDist ? best : null;
}

/**
 * Generates a ordered list of waypoints from a closed loop of rails.
 * Traverses connected rails to form a single continuous cycle of coordinates.
 */
export function generateLoopPathPoints(elements: LayoutElement[]): Pt[] {
  const rails = elements.filter(
    (e) => e.metadata.visible && (e.type === 'overhead-rail' || e.type === 'overhead-curve')
  );
  if (rails.length === 0) return [];

  const nodes: Pt[] = [];
  const tol2 = ENDPOINT_TOLERANCE * ENDPOINT_TOLERANCE;

  const findNode = (p: Pt): number => {
    for (let i = 0; i < nodes.length; i++) {
      const dx = nodes[i].x - p.x;
      const dy = nodes[i].y - p.y;
      if (dx * dx + dy * dy <= tol2) return i;
    }
    nodes.push({ ...p });
    return nodes.length - 1;
  };

  const railEndpoints = rails.map((rail) => {
    const [a, b] = getRailEndpoints(rail);
    return {
      rail,
      na: findNode(a),
      nb: findNode(b),
    };
  });

  const adj: Map<number, Array<{ railIndex: number; neighbor: number }>> = new Map();
  for (let i = 0; i < nodes.length; i++) {
    adj.set(i, []);
  }

  railEndpoints.forEach((re, railIdx) => {
    adj.get(re.na)!.push({ railIndex: railIdx, neighbor: re.nb });
    adj.get(re.nb)!.push({ railIndex: railIdx, neighbor: re.na });
  });

  const visitedNodes = new Set<number>();
  interface PathNode {
    node: number;
    railIndex: number;
  }
  const path: PathNode[] = [];

  let cycleStartNode = -1;
  let cyclePath: PathNode[] = [];

  const dfs = (curr: number, prevEdge: number): boolean => {
    visitedNodes.add(curr);

    const neighbors = adj.get(curr) || [];
    for (const edge of neighbors) {
      if (edge.railIndex === prevEdge) continue;

      if (visitedNodes.has(edge.neighbor)) {
        cycleStartNode = edge.neighbor;
        const cycleIdx = path.findIndex((p) => p.node === cycleStartNode);
        if (cycleIdx !== -1) {
          cyclePath = path.slice(cycleIdx);
        } else {
          cyclePath = [...path];
        }
        cyclePath.push({ node: curr, railIndex: edge.railIndex });
        return true;
      }

      path.push({ node: curr, railIndex: edge.railIndex });
      if (dfs(edge.neighbor, edge.railIndex)) return true;
      path.pop();
    }

    return false;
  };

  // Run DFS starting from node 0 (if there are any nodes)
  if (nodes.length > 0) {
    dfs(0, -1);
  }

  if (cyclePath.length === 0) {
    return [];
  }

  const waypoints: Pt[] = [];

  for (let i = 0; i < cyclePath.length; i++) {
    const step = cyclePath[i];

    const rail = rails[step.railIndex];
    const startNodeIdx = step.node;

    const startPt = nodes[startNodeIdx];

    const pts = getRailStopPath(rail);
    if (pts.length < 2) continue;

    const dStart0 = Math.hypot(pts[0].x - startPt.x, pts[0].y - startPt.y);
    const dEnd0 = Math.hypot(pts[pts.length - 1].x - startPt.x, pts[pts.length - 1].y - startPt.y);

    const orderedPts = dStart0 < dEnd0 ? pts : [...pts].reverse();

    for (let j = 0; j < orderedPts.length; j++) {
      if (j === 0 && waypoints.length > 0) {
        continue;
      }
      waypoints.push(orderedPts[j]);
    }
  }

  if (waypoints.length > 1) {
    const first = waypoints[0];
    const last = waypoints[waypoints.length - 1];
    const d = Math.hypot(last.x - first.x, last.y - first.y);
    if (d > 1e-3) {
      waypoints.push({ ...first });
    }
  }

  return waypoints;
}

/**
 * The 3D stocker model's shuttle/crane sits this fraction of the width off the
 * model's geometric centre along local X.
 */
const STOCKER_CRANE_X_RATIO = SHARED_STOCKER_CRANE_X_RATIO;

/**
 * Dock edge a ported element presents to the OHT lane. Stockers default to the
 * tray face; tools follow whichever edge their load ports actually sit on.
 */
export function getDockEdge(el: LayoutElement): PortEdge {
  const stored = el.customProperties?.ports;
  if (Array.isArray(stored) && stored.length > 0) {
    const counts: Record<PortEdge, number> = { top: 0, right: 0, bottom: 0, left: 0 };
    for (const port of stored as Port[]) counts[portEdge(port)] += 1;
    const first = portEdge(stored[0] as Port);
    return (Object.keys(counts) as PortEdge[]).reduce(
      (best, candidate) => (counts[candidate] > counts[best] ? candidate : best),
      first
    );
  }
  return el.type === 'stocker' ? STOCKER_PORT_EDGE : EQUIPMENT_PORT_EDGE;
}

/**
 * Dock line in the element's local frame, measured from the element origin
 * (top-left, which is also the Konva rotation pivot).
 *
 * Docks hang {@link STOCKER_TRAY_HANG_RATIO} beyond the dock edge so they land
 * under the OHT lane rather than on the element's own footprint — that standoff
 * is what lets the vehicle's hoist descend straight onto the port.
 */
export function getDockLineLocalOffset(el: LayoutElement): { lx: number; ly: number } {
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  // Centre-relative, and per kind: each ratio is the plane that kind's 3D kit
  // already hands the FOUP over on, so snapping the rail here lines the hoist
  // up with the dock instead of with the element's outline.
  const hang =
    el.type === 'stocker' ? STOCKER_TRAY_HANG_RATIO : EQUIPMENT_DOCK_HANG_RATIO;
  switch (getDockEdge(el)) {
    case 'top':
      return { lx: w / 2, ly: h / 2 - hang * h };
    case 'bottom':
      return { lx: w / 2, ly: h / 2 + hang * h };
    case 'left':
      return { lx: w / 2 - hang * w, ly: h / 2 };
    case 'right':
      return { lx: w / 2 + hang * w, ly: h / 2 };
  }
}

function getStockerTrayLocalOffset(el: LayoutElement): { lx: number; ly: number } {
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  return { lx: w / 2, ly: h / 2 - STOCKER_TRAY_HANG_RATIO * h };
}

/** Element centre in world space (the origin is the top-left, not the centre). */
function getStockerCenter(el: LayoutElement): Pt {
  const rad = ((el.rotation || 0) * Math.PI) / 180;
  return localToWorld(
    el.position.x,
    el.position.y,
    Math.cos(rad),
    Math.sin(rad),
    el.dimensions.width / 2,
    el.dimensions.height / 2
  );
}

export function getStockerTrayPosition(el: LayoutElement): Pt {
  const rad = ((el.rotation || 0) * Math.PI) / 180;
  const { lx, ly } = getStockerTrayLocalOffset(el);
  return localToWorld(el.position.x, el.position.y, Math.cos(rad), Math.sin(rad), lx, ly);
}

/** Centre of the element's dock line in world space (stocker tray or tool docks). */
export function getDockLinePosition(el: LayoutElement): Pt {
  const rad = ((el.rotation || 0) * Math.PI) / 180;
  const { lx, ly } = getDockLineLocalOffset(el);
  return localToWorld(el.position.x, el.position.y, Math.cos(rad), Math.sin(rad), lx, ly);
}

/**
 * Same as `getStockerTrayPosition` but shifted onto the 3D model's crane axis,
 * for use in 3D rendering only.
 */
export function getStockerTray3DPosition(el: LayoutElement): Pt {
  const rad = ((el.rotation || 0) * Math.PI) / 180;
  const { lx, ly } = getStockerTrayLocalOffset(el);
  return localToWorld(
    el.position.x,
    el.position.y,
    Math.cos(rad),
    Math.sin(rad),
    lx + STOCKER_CRANE_X_RATIO * el.dimensions.width,
    ly
  );
}

/**
 * OHT handoff target for a specific stocker load port (LP-1 / LP-2).
 * Uses the port's `rx` on the tray hang-off face so drop vs pick land on
 * the correct dock. Falls back to the generic tray when `portId` is missing.
 *
 * Dock selection goes through {@link resolveStockerDockIndexOrFirst} so this
 * lands on exactly the dock the 3D stocker kit animates — resolving the two
 * independently used to park the OHT on the crane axis while the FOUP appeared
 * at the leftmost dock.
 */
export function getStockerPort3DPosition(el: LayoutElement, portId?: string | null): Pt {
  if (!portId) return getStockerTray3DPosition(el);
  const ports = Array.isArray(el.customProperties?.ports)
    ? (el.customProperties!.ports as StockerDockRef[])
    : [];

  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const offsets = resolveStockerDockOffsetsX(w, ports, {
    outsideStocker: el.customProperties?.outsideStocker === true,
  });
  if (offsets.length === 0) return getStockerTray3DPosition(el);

  const dockX = offsets[resolveStockerDockIndexOrFirst(ports, portId, offsets.length)];
  if (typeof dockX !== 'number') return getStockerTray3DPosition(el);

  const rad = ((el.rotation || 0) * Math.PI) / 180;
  // Same hang-off as the OHT tray, but at this dock's horizontal offset.
  const lx = w / 2 + dockX;
  const ly = h / 2 - STOCKER_TRAY_HANG_RATIO * h;
  return localToWorld(el.position.x, el.position.y, Math.cos(rad), Math.sin(rad), lx, ly);
}

/**
 * Exact OHT operation point for a stocker/equipment port.
 *
 * Port identity selects the coordinate along the dock face; the perpendicular
 * coordinate is the same physical handoff line used by the 3D kit and rail
 * snapping. No proximity or selected-element fallback is permitted.
 */
export function getOhtDockPosition(
  el: LayoutElement,
  portId: string | null | undefined
): Pt | null {
  if (el.type === 'stocker') {
    if (!portId) return null;
    const stored = el.customProperties?.ports;
    const ports = Array.isArray(stored) ? (stored as Port[]) : [];
    const hasNumberedDock = /(\d+)\s*$/.test(portId);
    if (resolveStockerDockIndex(ports, portId) === null && !hasNumberedDock) {
      return null;
    }
    return getStockerPort3DPosition(el, portId);
  }
  const stored = el.customProperties?.ports;
  if (!Array.isArray(stored) || !portId) return null;
  // Same spacing the 2D markers and the 3D seats use, so the OHT stops over the
  // dock it is actually working rather than over the legacy default position.
  const docks = alignEquipmentPortsToLoadDocks([...(stored as Port[])], 1, 1);
  const port = docks.find((candidate) => candidate.id === portId);
  if (!port) return null;

  const { width: w, height: h } = el.dimensions;
  const line = getDockLineLocalOffset(el);
  const edge = getDockEdge(el);
  const lx = edge === 'top' || edge === 'bottom' ? port.rx * w : line.lx;
  const ly = edge === 'left' || edge === 'right' ? port.ry * h : line.ly;
  const rad = ((el.rotation || 0) * Math.PI) / 180;
  return localToWorld(el.position.x, el.position.y, Math.cos(rad), Math.sin(rad), lx, ly);
}

function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** World point in a straight/curve rail's local frame (origin = rail top-left). */
function worldToRailLocal(rail: LayoutElement, pt: Pt): { localX: number; localY: number } {
  const { x: tx, y: ty } = rail.position;
  const rotRad = ((rail.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rotRad);
  const sin = Math.sin(rotRad);
  const ddx = pt.x - tx;
  const ddy = pt.y - ty;
  return {
    localX: ddx * cos + ddy * sin,
    localY: -ddx * sin + ddy * cos,
  };
}

/** Signed perpendicular distance from a world point to the rail lane centre (rail-local Y). */
function railLanePerpendicularOffset(rail: LayoutElement, pt: Pt): number {
  const { localY } = worldToRailLocal(rail, pt);
  return localY - rail.dimensions.height / 2;
}

/** +1 / -1 = which side of the lane centre the point sits on; 0 = on the lane. */
function sideSignFromPerp(perp: number): number {
  if (Math.abs(perp) < 1e-6) return 0;
  return perp > 0 ? 1 : -1;
}

function railLaneSideSign(rail: LayoutElement, pt: Pt): number {
  return sideSignFromPerp(railLanePerpendicularOffset(rail, pt));
}

/**
 * Side of the rail from the operator's current drop pose only (dock line first,
 * then body centre). Never inferred from a prior snapped rotation.
 */
function getIntendedDockSideSign(el: LayoutElement, rail: LayoutElement): number {
  const trayPerp = railLanePerpendicularOffset(rail, getDockLinePosition(el));
  const centerPerp = railLanePerpendicularOffset(rail, getStockerCenter(el));
  const traySign = sideSignFromPerp(trayPerp);
  if (traySign !== 0) return traySign;
  const centerSign = sideSignFromPerp(centerPerp);
  if (centerSign !== 0) return centerSign;
  return sideSignFromPerp(trayPerp + centerPerp);
}

function computeDockSnapTopLeft(
  el: LayoutElement,
  snapWorld: Pt,
  rotation: number
): { x: number; y: number } {
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const { lx, ly } = getDockLineLocalOffset(el);
  return {
    x: snapWorld.x - (lx * cos - ly * sin),
    y: snapWorld.y - (lx * sin + ly * cos),
  };
}

function snapWorldToNearestStop(
  rail: LayoutElement,
  world: Pt,
  stops: unknown[] | undefined,
  radius: number
): Pt {
  if (!stops || stops.length === 0) return world;
  const railStops = stops.filter((s) => (s as { railId?: string }).railId === rail.id);
  let best: Pt | null = null;
  let bestDist = radius;
  for (const stop of railStops) {
    const stopWorld = railStopToWorld(rail, (stop as { distance: number }).distance);
    const stopDist = Math.hypot(world.x - stopWorld.x, world.y - stopWorld.y);
    if (stopDist < bestDist) {
      bestDist = stopDist;
      best = stopWorld;
    }
  }
  return best ?? world;
}

function placedBodySideSign(
  el: LayoutElement,
  rail: LayoutElement,
  snapWorld: Pt,
  rotation: number
): number {
  const pos = computeDockSnapTopLeft(el, snapWorld, rotation);
  const placed = { ...el, position: pos, rotation };
  return railLaneSideSign(rail, getStockerCenter(placed));
}

/**
 * Rotation that turns the element's dock face toward `snapWorld` on `rail`,
 * keeping the element on whichever side of the rail it already sits.
 *
 * The formula is written for a top-edge dock face (the stocker tray); other
 * dock edges are corrected by the difference in their outward direction so a
 * bottom- or side-docking tool still ends up presenting its ports to the lane.
 */
function getDockFacingRotation(el: LayoutElement, rail: LayoutElement, snapWorld: Pt): number {
  const center = getStockerCenter(el);
  const vx = center.x - snapWorld.x;
  const vy = center.y - snapWorld.y;
  const edgeCorrection = directionForEdge('top') - directionForEdge(getDockEdge(el));

  if (isCurveRailType(resolveRailType(rail))) {
    // Curves have no single axis — aim the dock face straight at the snap point.
    if (Math.hypot(vx, vy) < 1e-6) return normalizeDeg(el.rotation || 0);
    return normalizeDeg((Math.atan2(-vx, vy) * 180) / Math.PI + edgeCorrection);
  }

  const railRotDeg = rail.rotation || 0;
  const intendedSide = getIntendedDockSideSign(el, rail);
  const candidates = [
    normalizeDeg(railRotDeg + edgeCorrection),
    normalizeDeg(railRotDeg + 180 + edgeCorrection),
  ];

  if (intendedSide !== 0) {
    for (const rotation of candidates) {
      if (placedBodySideSign(el, rail, snapWorld, rotation) === intendedSide) return rotation;
    }
  }

  // Last resort: smallest translation from the drop pose (no prior-rotation bias).
  let bestRot = candidates[0]!;
  let bestMove = Infinity;
  for (const rotation of candidates) {
    const pos = computeDockSnapTopLeft(el, snapWorld, rotation);
    const move = Math.hypot(pos.x - el.position.x, pos.y - el.position.y);
    if (move < bestMove) {
      bestMove = move;
      bestRot = rotation;
    }
  }
  return bestRot;
}

export type StockerSnapResult = { x: number; y: number; rotation: number };

/**
 * Place a ported element so its dock line lands on the nearest OHT lane.
 *
 * Stockers have always done this; tools now share it, because a load port that
 * does not sit under the track gives the vehicle's hoist nowhere to descend.
 */
export function findDockSnapPosition(
  element: LayoutElement,
  others: LayoutElement[],
  tolerance: number = SNAP_DRAG_TOLERANCE,
  stops?: any[]
): StockerSnapResult | null {
  const rails = others.filter((el) => ALL_RAIL_TYPES.has(el.type));
  if (rails.length === 0) return null;

  const stocker = element;
  const tray = getDockLinePosition(element);
  const trayX = tray.x;
  const trayY = tray.y;

  let bestDist = Infinity;
  let bestSnapWorld: Pt | null = null;
  let bestRail: LayoutElement | null = null;

  for (const rail of rails) {
    const { x: tx, y: ty } = rail.position;
    const rotRad = ((rail.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rotRad);
    const sin = Math.sin(rotRad);

    const ddx = trayX - tx;
    const ddy = trayY - ty;
    const localX = ddx * cos + ddy * sin;
    const localY = -ddx * sin + ddy * cos;

    const railType = resolveRailType(rail);

    if (isCurveRailType(railType)) {
      const radius = getCurveRadius(rail.dimensions.width, rail.dimensions.height);
      const sweepDeg = getCurveSweepSign(rail) * getCurveSweepAngle(railType);
      const sweepMin = Math.min(0, sweepDeg);
      const sweepMax = Math.max(0, sweepDeg);

      const polarDeg = Math.atan2(localY, localX) * (180 / Math.PI);
      if (polarDeg < sweepMin - 4 || polarDeg > sweepMax + 4) continue;
      const clampedRad = (Math.max(sweepMin, Math.min(sweepMax, polarDeg)) * Math.PI) / 180;

      const snapLocalX = radius * Math.cos(clampedRad);
      const snapLocalY = radius * Math.sin(clampedRad);
      let world = localToWorld(tx, ty, cos, sin, snapLocalX, snapLocalY);

      world = snapWorldToNearestStop(rail, world, stops, 50);

      const dist = Math.hypot(trayX - world.x, trayY - world.y);

      if (dist < bestDist) {
        bestDist = dist;
        bestSnapWorld = world;
        bestRail = rail;
      }
    } else {
      const { width: tw, height: th } = rail.dimensions;
      if (localX < -tolerance || localX > tw + tolerance) continue;
      const clampedX = Math.max(0, Math.min(tw, localX));
      const laneY = th / 2;

      let world = localToWorld(tx, ty, cos, sin, clampedX, laneY);

      world = snapWorldToNearestStop(rail, world, stops, 50);

      const dist = Math.hypot(trayX - world.x, trayY - world.y);

      if (dist < bestDist) {
        bestDist = dist;
        bestSnapWorld = world;
        bestRail = rail;
      }
    }
  }

  if (bestSnapWorld && bestRail && bestDist <= tolerance) {
    // Turn the element to face the rail first, then place it so the dock line —
    // in that new orientation — lands exactly on the snap point.
    const rotation = getDockFacingRotation(stocker, bestRail, bestSnapWorld);
    const position = computeDockSnapTopLeft(stocker, bestSnapWorld, rotation);
    return { ...position, rotation };
  }

  return null;
}

/** @deprecated Prefer {@link findDockSnapPosition} — tools snap the same way now. */
export const findStockerSnapPosition = findDockSnapPosition;

/**
 * Elements that present load docks to the OHT lane.
 * Tools only qualify once they actually carry ports, which keeps plain
 * rectangles and annotation shapes free to sit anywhere.
 */
export function hasOhtDocks(el: LayoutElement): boolean {
  if (el.type === 'stocker') return true;
  if (!isLayoutEquipmentElement(el)) return false;
  const ports = el.customProperties?.ports;
  return Array.isArray(ports) && ports.length > 0;
}

/**
 * Auto rail-dock snap is stocker-only. Process tools stay where the operator
 * placed them in the 2D editor.
 */
export function shouldAutoDockSnap(el: LayoutElement): boolean {
  return el.type === 'stocker';
}

/** Process tools skip grid / object / dock snap so they stay where drawn. */
export function shouldSkipEquipmentEditorSnap(el: LayoutElement): boolean {
  return isLayoutEquipmentElement(el) && el.type !== 'stocker';
}

/** Pull-in radius when a stocker is first placed, not just during a live drag. */
export const EQUIPMENT_DOCK_AUTO_SNAP_TOLERANCE = 240;

/**
 * Ride the nearest rail when a stocker is created or dropped.
 * Process equipment is left at the user-supplied pose.
 */
export function applyNearestDockSnap(
  element: LayoutElement,
  others: LayoutElement[],
  tolerance: number = EQUIPMENT_DOCK_AUTO_SNAP_TOLERANCE
): LayoutElement {
  if (!shouldAutoDockSnap(element)) return element;
  const snap = findDockSnapPosition(element, others, tolerance);
  if (!snap) return element;
  return {
    ...element,
    position: { ...element.position, x: snap.x, y: snap.y },
    rotation: snap.rotation,
  };
}

function layoutOriginToAxisPolygon(
  origin: Pt,
  width: number,
  height: number,
  rotationDeg: number
): Pt[] {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const cx = origin.x + (cos * width) / 2 - (sin * height) / 2;
  const cy = origin.y + (sin * width) / 2 + (cos * height) / 2;
  const halfW = width / 2;
  const halfH = height / 2;
  return [
    { x: cx - halfW, y: cy - halfH },
    { x: cx + halfW, y: cy - halfH },
    { x: cx + halfW, y: cy + halfH },
    { x: cx - halfW, y: cy + halfH },
  ];
}

function layoutElementForDrawnFootprint(polygon: Pt[], rotation: number): LayoutElement {
  const instance: EquipmentInstance = {
    id: '__draw_dock_snap__',
    templateId: null,
    name: '',
    objectType: 'equipment',
    category: 'Photolithography',
    equipmentType: 'PHOTO',
    description: '',
    tags: [],
    notes: '',
    polygon,
    rotation,
    overrides: {},
    metadata: {},
    createdAt: '',
    updatedAt: '',
  };
  return equipmentInstanceToLayoutElement(instance, null);
}

/**
 * After a two-click equipment rectangle, pull the footprint onto the nearest OHT
 * lane so default load ports sit under the track (same dock math as layout tools).
 */
export function snapEquipmentPolygonToNearestRail(
  polygon: Pt[],
  rotation: number,
  others: LayoutElement[] = [],
  tolerance: number = EQUIPMENT_DOCK_AUTO_SNAP_TOLERANCE,
  stops?: unknown[]
): { polygon: Pt[]; rotation: number } {
  if (polygon.length < 3 || others.length === 0) return { polygon, rotation };

  const bounds = polygonBounds(polygon);
  const width = Math.max(0.5, bounds.maxX - bounds.minX);
  const height = Math.max(0.5, bounds.maxY - bounds.minY);

  const element = layoutElementForDrawnFootprint(polygon, rotation);
  if (!hasOhtDocks(element)) return { polygon, rotation };

  const snap = findDockSnapPosition(element, others, tolerance, stops as any[]);
  if (!snap) return { polygon, rotation };

  const nextPolygon = layoutOriginToAxisPolygon(
    { x: snap.x, y: snap.y },
    width,
    height,
    snap.rotation
  );
  return { polygon: nextPolygon, rotation: snap.rotation };
}
